import { useEffect, useState } from 'react';
import http from '../services/http';

/**
 * O nome curto do representante, para caber ao lado do nome do colaborador.
 *
 * ## De onde vem
 *
 * De `companies.service_group_description` — o mesmo campo que a tela do
 * chamado mostra como "Rep.". Ele guarda, dependendo do cadastro no ERP, o
 * nome de uma PESSOA (`NEY ELTON BARBOSA NOGUEIRA`) ou de uma EMPRESA
 * (`J E C COM REP. E SERV. DE INFOR LTDA`). É essa mistura que impede uma
 * regra ingênua de servir aos dois.
 *
 * ## A regra
 *
 * O primeiro nome — foi o que o dono do produto pediu, e "Ney" é mesmo o
 * nome da pessoa, não um apelido que alguém inventou.
 *
 * Com um ajuste para o caso das INICIAIS: quando o nome começa com letras
 * soltas, a primeira palavra sozinha vira "J", que não identifica ninguém.
 * Nesse caso elas são agrupadas (`J E C`). Sem isso, dois representantes
 * cujos nomes comecem com iniciais diferentes apareceriam como "J" e "A" —
 * tecnicamente corretos e inúteis.
 */

/**
 * Palavras que não identificam ninguém e só ocupam espaço.
 *
 * Só as de empresa, e só quando sobra algo depois de tirá-las: um
 * representante cadastrado literalmente como "LTDA" é improvável, mas
 * devolver vazio seria pior que devolver "LTDA".
 */
const RUIDO = ['LTDA', 'ME', 'EPP', 'EIRELI', 'S/A', 'SA', 'CIA'];

const limpar = (palavra) => palavra.replace(/[^\p{L}\p{N}/]/gu, '');

/** Iniciais soltas: uma letra, ou uma letra com ponto. */
const ehInicial = (palavra) => limpar(palavra).length === 1;

const capitalizar = (texto) => texto
    .toLocaleLowerCase('pt-BR')
    .replace(/(^|\s)(\p{L})/gu, (_, antes, letra) => antes + letra.toLocaleUpperCase('pt-BR'));

/**
 * @param {string} nomeCompleto  como está no ERP
 * @returns {string} o nome curto, ou '' quando não há nome
 */
export const nomeCurtoDoRepresentante = (nomeCompleto) => {
    const palavras = String(nomeCompleto || '')
        .trim()
        .split(/\s+/)
        .filter((p) => limpar(p) !== '');

    if (palavras.length === 0) return '';

    const uteis = palavras.filter((p) => !RUIDO.includes(limpar(p).toUpperCase()));
    const base = uteis.length > 0 ? uteis : palavras;

    // Iniciais soltas no começo: agrupa todas elas. `J E C COM REP...` vira
    // `J E C`, e não `J`.
    if (ehInicial(base[0])) {
        const iniciais = [];
        for (const palavra of base) {
            if (!ehInicial(palavra)) break;
            iniciais.push(limpar(palavra).toUpperCase());
        }
        return iniciais.join(' ');
    }

    return capitalizar(limpar(base[0]));
};

/**
 * O nome do colaborador com o representante ao lado.
 *
 * O prefixo "Repre" não é enfeite: sem ele, `Lucio (Ney)` não diz o que Ney
 * é — podia ser o responsável, o cliente, o departamento. É também
 * literalmente o que a Gisele já escrevia à mão.
 *
 * **Não duplica.** Os nomes que ela já editou à mão trazem o trecho dentro do
 * próprio nome, e acrescentar de novo daria `Lucio (Repre Ney) (Repre Ney)`.
 */
export const nomeComRepresentante = (nomeDoColaborador, nomeDoRepresentante) => {
    const nome = String(nomeDoColaborador || '').trim();
    const curto = nomeCurtoDoRepresentante(nomeDoRepresentante);

    if (nome === '' || curto === '') return nome;

    // Comparação sem acento e sem caixa: quem digitou "repre ney" à mão tem
    // de contar como já tendo o trecho.
    const semAcento = (t) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('pt-BR');

    if (semAcento(nome).includes(semAcento(curto))) return nome;

    return `${nome} (Repre ${curto})`;
};

/**
 * O mapa código → nome do representante, buscado UMA vez por sessão.
 *
 * Mesmo padrão de `useFasesDoRelacionamento`, e pelo mesmo motivo: a lista
 * aparece em telas diferentes e se repete por chamado na listagem. Sem o
 * cache seriam N requisições para a mesma lista curta, que quase nunca muda.
 *
 * Mora aqui, e não no payload do chamado, porque o nome do representante sai
 * de `companies.service_group_description` e viria por um join POR CHAMADO.
 * O chamado traz só o código; o nome é resolvido no navegador.
 */
let promessaDosRepresentantes = null;

export const useRepresentantes = () => {
    const [porCodigo, setPorCodigo] = useState({});

    useEffect(() => {
        let cancelado = false;

        if (!promessaDosRepresentantes) {
            promessaDosRepresentantes = http.get('/companies/representantes')
                .then(({ data }) => {
                    const mapa = {};
                    (Array.isArray(data) ? data : []).forEach((c) => {
                        if (c?.codigo != null) mapa[String(c.codigo)] = c.nome;
                    });
                    return mapa;
                })
                // Falha NÃO fica em cache: um erro de rede momentâneo não pode
                // deixar o rótulo ausente pelo resto da sessão. E o resultado
                // de falhar é o nome SEM o representante -- nunca a tela
                // quebrada.
                .catch(() => { promessaDosRepresentantes = null; return {}; });
        }

        promessaDosRepresentantes.then((mapa) => {
            if (!cancelado) setPorCodigo(mapa);
        });

        return () => { cancelado = true; };
    }, []);

    return porCodigo;
};
